import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    connectSocket,
    joinUsuariosOnlineRoom,
    leaveUsuariosOnlineRoom,
    onSocketEvent,
} from '../services/socket';
import {
    fetchUsuariosOnline,
    fetchUsuariosOnlineHistory,
    fetchUsuariosOnlineMetrics,
} from '../redux/actions/usuariosOnline';
import {
    DEFAULT_CHART_PERIOD,
    EMPTY_ONLINE_METRICS,
    normalizeChartMetrics,
} from '../pages/usuariosOnline/usuariosOnline.constants';

const HISTORY_REFRESH_REASONS = new Set([
    'LOGIN',
    'LOGOUT',
    'SESSION_STARTED',
    'SESSION_ENDED',
    'SESSION_EXPIRED',
    'CONNECTION_LOST',
    'CONNECTION_RESTORED',
]);

const LIVE_EMPTY = {
    currentlyOnline: 0,
    uniqueConnectedToday: 0,
    activeSessions: 0,
};

const hasPeriod = (cache, period) => (
    Object.prototype.hasOwnProperty.call(cache, period)
);

const applyLiveFromSnapshot = (prev, snapshotMetrics) => {
    if (!snapshotMetrics) return prev;
    return {
        currentlyOnline: snapshotMetrics.currentlyOnline ?? prev.currentlyOnline,
        uniqueConnectedToday:
            snapshotMetrics.uniqueConnectedToday
            ?? snapshotMetrics.uniqueConnected
            ?? prev.uniqueConnectedToday,
        activeSessions: snapshotMetrics.activeSessions ?? prev.activeSessions,
    };
};

const uniquePeriods = (chartPeriods) => (
    [...new Set(Object.values(chartPeriods || {}).filter(Boolean))]
);

export const useUsuariosOnlineDashboard = ({
    chartPeriods = {},
    historyQuery,
    historyReady,
}) => {
    const [liveMetrics, setLiveMetrics] = useState(LIVE_EMPTY);
    const [onlineUsers, setOnlineUsers] = useState([]);
    const [chartByPeriod, setChartByPeriod] = useState({});
    const [chartLoadingByPeriod, setChartLoadingByPeriod] = useState({});
    const [historyRows, setHistoryRows] = useState([]);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [snapshotLoading, setSnapshotLoading] = useState(true);
    const [historyLoading, setHistoryLoading] = useState(true);
    const [error, setError] = useState('');

    const historyQueryRef = useRef(historyQuery);
    const chartPeriodsRef = useRef(chartPeriods);
    const chartByPeriodRef = useRef(chartByPeriod);
    const chartInFlightRef = useRef(new Set());
    const chartRequestIdRef = useRef({});
    const historyRequestIdRef = useRef(0);

    const historyQueryKey = JSON.stringify(historyQuery || {});
    const activePeriods = useMemo(() => uniquePeriods(chartPeriods), [chartPeriods]);
    const activePeriodsKey = activePeriods.slice().sort().join(',');

    historyQueryRef.current = historyQuery;
    chartPeriodsRef.current = chartPeriods;
    chartByPeriodRef.current = chartByPeriod;

    const applySnapshot = useCallback((payload) => {
        if (!payload) return;
        if (Array.isArray(payload.onlineUsers)) setOnlineUsers(payload.onlineUsers);
        if (payload.metrics) {
            setLiveMetrics((prev) => applyLiveFromSnapshot(prev, payload.metrics));
        }
    }, []);

    const loadSnapshot = useCallback(async () => {
        setSnapshotLoading(true);
        setError('');
        try {
            const data = await fetchUsuariosOnline();
            applySnapshot(data);
        } catch (err) {
            setError(err?.response?.data?.message || 'No se pudo cargar usuarios online.');
        } finally {
            setSnapshotLoading(false);
        }
    }, [applySnapshot]);

    const loadChartPeriod = useCallback(async (period, {
        silent = false,
        onlyIfMissing = false,
    } = {}) => {
        if (!period) return;

        if (onlyIfMissing) {
            if (hasPeriod(chartByPeriodRef.current, period) || chartInFlightRef.current.has(period)) {
                return;
            }
        }

        const requestId = (chartRequestIdRef.current[period] || 0) + 1;
        chartRequestIdRef.current[period] = requestId;
        chartInFlightRef.current.add(period);

        if (!silent) {
            setChartLoadingByPeriod((prev) => ({ ...prev, [period]: true }));
        }

        try {
            const data = await fetchUsuariosOnlineMetrics({
                period,
                userCategory: 'all',
            });
            if (chartRequestIdRef.current[period] !== requestId) return;
            setChartByPeriod((prev) => ({
                ...prev,
                [period]: normalizeChartMetrics(data?.metrics, period),
            }));
        } catch (err) {
            if (chartRequestIdRef.current[period] !== requestId) return;
            setChartByPeriod((prev) => ({
                ...prev,
                [period]: normalizeChartMetrics(null, period),
            }));
            setError(err?.response?.data?.message || 'No se pudieron cargar las métricas.');
        } finally {
            chartInFlightRef.current.delete(period);
            if (!silent && chartRequestIdRef.current[period] === requestId) {
                setChartLoadingByPeriod((prev) => ({ ...prev, [period]: false }));
            }
        }
    }, []);

    const loadHistory = useCallback(async (query, { silent = false } = {}) => {
        const requestId = historyRequestIdRef.current + 1;
        historyRequestIdRef.current = requestId;
        if (!silent) setHistoryLoading(true);
        try {
            const data = await fetchUsuariosOnlineHistory(query);
            if (historyRequestIdRef.current !== requestId) return;
            setHistoryRows(Array.isArray(data?.history) ? data.history : []);
            setHistoryTotal(data?.total || 0);
        } catch (err) {
            if (historyRequestIdRef.current !== requestId) return;
            setError(err?.response?.data?.message || 'No se pudo cargar el histórico de conexiones.');
        } finally {
            if (!silent && historyRequestIdRef.current === requestId) {
                setHistoryLoading(false);
            }
        }
    }, []);

    const refreshActiveCharts = useCallback(async ({ silent = true } = {}) => {
        const periods = uniquePeriods(chartPeriodsRef.current);
        await Promise.all(periods.map((period) => loadChartPeriod(period, { silent })));
    }, [loadChartPeriod]);

    useEffect(() => {
        loadSnapshot();
    }, [loadSnapshot]);

    // Solo pide periodos que aún no están en caché (evita recargar las otras tarjetas).
    useEffect(() => {
        if (!activePeriodsKey) return undefined;
        activePeriods.forEach((period) => {
            loadChartPeriod(period, { onlyIfMissing: true });
        });
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by activePeriodsKey
    }, [activePeriodsKey, loadChartPeriod]);

    useEffect(() => {
        if (!historyReady) return undefined;
        loadHistory(historyQueryRef.current);
        return undefined;
    }, [historyQueryKey, historyReady, loadHistory]);

    useEffect(() => {
        let mounted = true;
        const unsubs = [];

        const setup = async () => {
            try {
                await connectSocket();
                if (!mounted) return;
                joinUsuariosOnlineRoom();
                unsubs.push(onSocketEvent('usuarios_online_update', (payload) => {
                    if (!mounted) return;
                    applySnapshot(payload);
                    if (!HISTORY_REFRESH_REASONS.has(payload?.reason)) return;
                    const current = historyQueryRef.current;
                    if (current?.page === 0) {
                        loadHistory(current, { silent: true });
                    }
                    refreshActiveCharts({ silent: true });
                }));
                unsubs.push(onSocketEvent('connect', () => {
                    if (!mounted) return;
                    joinUsuariosOnlineRoom();
                }));
            } catch {
                if (mounted) {
                    setError((prev) => prev || 'No se pudo suscribir a las actualizaciones en tiempo real.');
                }
            }
        };

        setup();

        return () => {
            mounted = false;
            leaveUsuariosOnlineRoom();
            unsubs.forEach((off) => off?.());
        };
    }, [applySnapshot, loadHistory, refreshActiveCharts]);

    const rolePeriod = chartPeriods.role || DEFAULT_CHART_PERIOD;
    const devicePeriod = chartPeriods.device || DEFAULT_CHART_PERIOD;
    const barPeriod = chartPeriods.bar || DEFAULT_CHART_PERIOD;

    const isChartLoading = (period) => (
        Boolean(chartLoadingByPeriod[period]) || !hasPeriod(chartByPeriod, period)
    );

    const reload = useCallback(async () => {
        await Promise.all([
            loadSnapshot(),
            refreshActiveCharts({ silent: false }),
            historyReady ? loadHistory(historyQueryRef.current) : Promise.resolve(),
        ]);
    }, [historyReady, loadHistory, loadSnapshot, refreshActiveCharts]);

    return {
        liveMetrics,
        onlineUsers,
        roleMetrics: chartByPeriod[rolePeriod] || EMPTY_ONLINE_METRICS,
        deviceMetrics: chartByPeriod[devicePeriod] || EMPTY_ONLINE_METRICS,
        barMetrics: chartByPeriod[barPeriod] || EMPTY_ONLINE_METRICS,
        roleLoading: isChartLoading(rolePeriod),
        deviceLoading: isChartLoading(devicePeriod),
        barLoading: isChartLoading(barPeriod),
        historyRows,
        historyTotal,
        snapshotLoading,
        historyLoading,
        error,
        reload,
    };
};
